import { useRef, useState } from "react";
import BarCode from "../BarCode/BarCode";
import "./BarCodesPreview.css";
import { Button } from "antd";
import QrCode from "../QrCode/QrCode";

const GLOBAL_MAX_CODES_PER_PAGE = 6;
const PORTRAIT_MAX_CODES_PER_PAGE = 4;

// Function to chunk array into smaller arrays of size n
const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const createPrintIframe = (content) => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "absolute";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(content);
  doc.close();

  return iframe;
};

const getGridConfig = (count, orientation) => {
  if (count <= 0) count = 1;

  if (orientation === "portrait") {
    return {
      gridTemplateColumns: "1fr",
      gridTemplateRows: `repeat(${count}, 1fr)`,
      display: "grid"
    };
  } else {
    let cols = 1;
    let rows = 1;

    if (count === 1) { cols = 1; rows = 1; }
    else if (count === 2) { cols = 2; rows = 1; }
    else if (count <= 4) { cols = 2; rows = 2; }
    else { cols = 2; rows = 3; }

    return {
      display: "grid",
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gridTemplateRows: `repeat(${rows}, 1fr)`,
    };
  }
};

const generatePrintHTML = (printableContent, orientation, effectiveCodesPerPage) => {
  const gridConfig = getGridConfig(effectiveCodesPerPage, orientation);
  const gridCss = `
    display: ${gridConfig.display};
    grid-template-columns: ${gridConfig.gridTemplateColumns};
    grid-template-rows: ${gridConfig.gridTemplateRows};
  `;

  return `
  <html>
    <head>
      <title>Barcodes Print</title>
      <style>
        @media print {
          body { margin: 0; padding: 0; visibility: visible; }
          
          @page {
            size: ${orientation}; /* Explicitly set landscape or portrait */
            margin: 0;
          }
          
          .barcode-page-wrapper {
            width: 100vw; /* Use viewport units to fill the physical page defined by @page */
            height: 100vh;
            margin: 0;
            padding: 2.5%; /* Relative padding */
            box-sizing: border-box;
            page-break-after: always;
            gap: 5mm; 
            ${gridCss}
            align-items: center;
            justify-items: center;
            overflow: hidden;
            display: grid; /* Already in gridCss but safer to double ensure layout stability */
          }
          
          .barcode-page-wrapper:last-child {
            page-break-after: auto;
          }
          
          .barcode-item {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            box-sizing: border-box;
          }

          svg[data-barcode] {
             display: block;
             width: 90% !important; 
             height: auto !important;
             max-height: 80%; 
          }

          .barcode-label {
            font-size: 8mm; 
            text-align: center;
            margin-top: 4mm;
            white-space: nowrap;
            font-weight: bold;
            flex-shrink: 0;
          }
          
          .qr-wrap {
            width: 100%; 
            height: 75%; 
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            flex-shrink: 1;
          }
          
          .qr-wrap canvas, .qr-wrap svg {
            height: 100% !important; 
            width: auto !important; 
            aspect-ratio: 1/1;
            display: block;
          }
        }
        
        body { margin: 0; padding: 0; visibility: hidden; }
        
        @media print {
          body { visibility: visible; }
        }
      </style>
    </head>
    <body>
      ${printableContent.join("")}
    </body>
  </html>
`;
};

const BarCodesPreview = ({ barcodes = [], name, labelType }) => {
  const [codesPerPage, setCodesPerPage] = useState("");
  const [orientation, setOrientation] = useState("landscape");
  const [maxCodesLimit, setMaxCodesLimit] = useState(GLOBAL_MAX_CODES_PER_PAGE);
  const containerRef = useRef(null);

  const getBarcodeDimensions = (actualCodesPerPage) => {
    const count = actualCodesPerPage === "" ? maxCodesLimit : actualCodesPerPage;

    let width = 2.0;
    let height = 120;
    let fontSize = 24;

    if (orientation === "portrait") {
      if (count <= 2) { height = 200; fontSize = 32; width = 3.0; }
      else if (count <= 4) { height = 150; fontSize = 28; width = 2.5; }
      else { height = 120; fontSize = 24; width = 2.0; }
    } else {
      if (count === 1) { width = 4.0; height = 250; fontSize = 48; }
      else if (count === 2) { width = 3.0; height = 200; fontSize = 36; }
      else if (count <= 4) { width = 2.5; height = 150; fontSize = 30; }
      else { width = 2.0; height = 120; fontSize = 24; }
    }

    return { width, height, fontSize };
  };

  const preparePrintableContent = () => {
    if (!barcodes.length) return [];
    const count = codesPerPage === "" ? maxCodesLimit : codesPerPage;
    const safeCount = (count <= 0) ? 1 : count;
    const chunks = chunkArray(barcodes, safeCount);

    return chunks.map(chunk => `
      <div class="barcode-page-wrapper">
        ${chunk.map(barcode => `
          <div class="barcode-item">
            ${labelType === "BARCODE"
        ? `<svg data-barcode data-code="${barcode.code}"></svg>`
        : `<div class="qr-wrap" data-qrcode data-value="${barcode.code}"></div>`
      }
            ${labelType === "BARCODE"
        ? ``
        : `<div class="barcode-label">${barcode.info || barcode.code}</div>`
      }
          </div>
        `).join("")}
      </div>`
    );
  };

  const onDownloadBarcodes = (event) => {
    if (event) event.preventDefault();
    if (!barcodes.length) return;

    if (labelType === "BARCODE" && !window.JsBarcode) {
      console.error("JsBarcode library not found!");
      return;
    }

    const effectiveCodesPerPage = codesPerPage === "" ? maxCodesLimit : codesPerPage;
    const safeCount = (effectiveCodesPerPage <= 0) ? 1 : effectiveCodesPerPage;

    const printableContent = preparePrintableContent();
    const printHTML = generatePrintHTML(printableContent, orientation, safeCount);
    const iframe = createPrintIframe(printHTML);

    iframe.onload = () => {
      const doc = iframe.contentDocument || iframe.contentWindow.document;

      if (labelType === "BARCODE") {
        const barcodeContainers = doc.querySelectorAll("[data-barcode]");
        const dimensions = getBarcodeDimensions(safeCount);

        barcodeContainers.forEach((svgEl) => {
          const code = svgEl.getAttribute("data-code");
          try {
            window.JsBarcode(svgEl, code, {
              width: dimensions.width,
              height: dimensions.height,
              lineColor: "#000",
              format: "CODE128",
              displayValue: true,
              margin: 5,
              fontSize: dimensions.fontSize,
              background: "#fff",
              textAlign: "center",
              fontOptions: "bold",
              font: "monospace",
            });
          } catch (e) {
            console.error("Barcode Render Error", e);
          }
        });
      }

      try {
        const previewItems = containerRef.current.querySelectorAll(".scannableLabel-wrapper");
        const printItems = doc.querySelectorAll(".barcode-item");

        let previewIdx = 0;
        printItems.forEach((printNode) => {
          const qrPlaceholder = printNode.querySelector("[data-qrcode]");
          if (!qrPlaceholder) return;

          const previewNode = previewItems[previewIdx];
          previewIdx++;

          if (!previewNode) return;

          const canvas = previewNode.querySelector("canvas");
          const svg = previewNode.querySelector("svg");

          if (svg) {
            qrPlaceholder.innerHTML = svg.outerHTML;
          } else if (canvas) {
            const newCanvas = document.createElement("canvas");
            newCanvas.width = canvas.width;
            newCanvas.height = canvas.height;
            newCanvas.getContext("2d").drawImage(canvas, 0, 0);
            qrPlaceholder.appendChild(newCanvas);
          }
        });
      } catch (err) {
        console.error("QR COPY ERROR:", err);
      }

      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error("Print Error", e);
        }
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      }, 200);
    };
  };

  const renderLocations = () => {
    if (!barcodes.length)
      return <div className="empty-locations">Locations not configured</div>;

    return (
      <div className="scannableType-labels-wrapper" ref={containerRef}>
        {barcodes.map(({ binId, code, info }) => (
          <div key={binId} className="scannableLabel-wrapper">
            {labelType === "BARCODE" ? (
              <BarCode text={code} info={info} />
            ) : (
              <QrCode value={code} />
            )}
          </div>
        ))}
      </div>
    );
  };

  const onCodesPerPageChange = (e) => {
    let val = e.target.value === "" ? "" : parseInt(e.target.value, 10);
    if (val !== "" && (isNaN(val) || val < 0)) val = "";
    if (val !== "" && val > maxCodesLimit) val = maxCodesLimit;
    setCodesPerPage(val);
  };

  const onOrientationChange = (newOrientation) => {
    setOrientation(newOrientation);
    if (newOrientation === "portrait") {
      setMaxCodesLimit(PORTRAIT_MAX_CODES_PER_PAGE);
      setCodesPerPage(prev => (prev !== "" && prev > PORTRAIT_MAX_CODES_PER_PAGE) ? PORTRAIT_MAX_CODES_PER_PAGE : prev);
    } else {
      setMaxCodesLimit(GLOBAL_MAX_CODES_PER_PAGE);
    }
  };

  const getCurrentCodesPerPageInfo = () => {
    if (codesPerPage === "") return `Auto (${maxCodesLimit} per page)`;
    return codesPerPage === 0 ? "Single per page" : `${codesPerPage} per page`;
  };

  return (
    <div className="scannableType-labels-container">
      <div className="scannableType-labels-inner">
        <header className="scannableType-labels-header">
          <h4 className="scannableType-labels-heading">
            {labelType === "BARCODE" ? "Barcodes" : "QR Codes"}{" "}
            {name ? `for ${name}` : ""}
          </h4>
        </header>
        {renderLocations()}
      </div>

      {barcodes.length > 0 && (
        <div className="scannableType-labels-actions" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "flex-start", flexDirection: "column", gap: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <label style={{ marginRight: 8 }}>
                {labelType === "BARCODE" ? "Barcodes" : "QR Codes"} per page:
                <div>
                  <input
                    type="number"
                    min={0}
                    max={maxCodesLimit}
                    value={codesPerPage}
                    onChange={onCodesPerPageChange}
                    placeholder="Auto"
                    style={{ width: 80, marginLeft: 5 }}
                    aria-label="Codes per page"
                  />
                </div>
              </label>

              <label style={{ marginRight: 8 }}>
                Orientation:
                <div>
                  <select
                    value={orientation}
                    onChange={(e) => onOrientationChange(e.target.value)}
                    style={{ width: 120, marginLeft: 5, padding: "4px 8px" }}
                  >
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                  </select>
                </div>
              </label>
            </div>

            <div style={{ fontSize: "12px", color: "#666" }}>
              <strong>Current setting:</strong> {getCurrentCodesPerPageInfo()} in {orientation} orientation
              {codesPerPage === "" && ` (Maximum ${maxCodesLimit} barcodes per page)`}
              {codesPerPage > 0 && orientation === "portrait" && " - arranged vertically"}
              {codesPerPage > 0 && orientation === "landscape" && " - arranged in grid"}
            </div>
          </div>

          <Button
            size="medium"
            onClick={onDownloadBarcodes}
            disabled={!barcodes.length}
            style={{ backgroundColor: "#607ad6", color: "white" }}
          >
            Print
          </Button>
        </div>
      )}
    </div>
  );
};

export default BarCodesPreview;
